import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import dragDropTypes from '@components/common/drop_zone/drag_drop_types';
import { useDrag, useDrop } from 'react-dnd';
import { updatePortalCard } from '@api/v4/private/portal_cards';
import { PortalCardTypes } from '@api/v4/private/PortalCardTypes';
import { TertiaryIconButton } from '@components/library/button/index';
import TileForm from './tile_form';

interface TileProps {
  description: string;
  displayWidth: string;
  draggedIndex: number;
  editMode: boolean;
  imageUrl: string;
  linkUrl: string;
  name: string;
  portalCardId: string;
  triggerCardsRefresh: () => void;
}

interface HoveredItem {
  id: string;
  insertedCardName: string;
  insertedCardId: string;
  startingIndex: number;
  type: string;
}

const Tile: React.FunctionComponent<TileProps> = ({
  description,
  displayWidth,
  draggedIndex,
  editMode,
  imageUrl,
  linkUrl,
  name,
  portalCardId,
  triggerCardsRefresh
}) => {
  const [showForm, setShowForm] = useState(false);
  const toggleForm = (): void => setShowForm((prevState) => !prevState);
  const previewRef = useRef(null);
  const dragRef = useRef(null);
  const placeholderImage = "https://cdn.brandfolder.io/27C9EC93/at/q6y18n-8wjz1k-fi9mwk/tile-image-placeholder.png";
  const [insertTileIndicator, setInsertTileIndicator] = useState(null);

  const updatePosition = (insertedCardId: string, insertedCardName: string, position: number): void => {
    updatePortalCard(insertedCardId, {
      name: insertedCardName,
      position,
      type: PortalCardTypes.PortalTile
    }).then(triggerCardsRefresh);
  };

  const [{ isHovered }, drop] = useDrop({
    accept: dragDropTypes.TILE,
    collect: (monitor) => ({
      isHovered: monitor.isOver()
    }),
    drop(item: HoveredItem) {
      const { startingIndex, insertedCardId, insertedCardName } = item;
      if (startingIndex !== draggedIndex) {
        updatePosition(insertedCardId, insertedCardName, draggedIndex);
      }
    },

    hover: (item, monitor) => {
      if (draggedIndex === item.startingIndex) {
        return;
      }

      const isOver = monitor.isOver();

      const right = draggedIndex > item.startingIndex;
      const left = draggedIndex < item.startingIndex;

      let insertCaretDirection = '';

      if (left) {
        insertCaretDirection = 'left';
      }

      if (right) {
        insertCaretDirection = 'right';
      }

      if (isOver && insertCaretDirection) {
        setInsertTileIndicator(`select-${insertCaretDirection}`);
      } else {
        setInsertTileIndicator(null);
      }
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    item: {
      startingIndex: draggedIndex,
      type: dragDropTypes.TILE,
      insertedCardId: portalCardId,
      insertedCardName: name
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
  });

  if (editMode) {
    drop(preview(previewRef));
    drag(dragRef);
  }

  const renderTile = (): JSX.Element => (
    <div
      className={classnames(
        "tiles__item",
        `tiles__item--${displayWidth}-width`,
        { [`${insertTileIndicator}`]: isHovered && insertTileIndicator },
        { dragging: isDragging }
      )}
      ref={previewRef}
    >
      <button
        className={`tiles__item--reorder-button card-button ${editMode ? "" : "hidden"}`}
        ref={dragRef}
        type="button"
      >
        <span className="bff-reorder" />
      </button>
      {editMode && (
        <TertiaryIconButton
          className="tiles__item--edit-settings-button card-button"
          height={50}
          icon="bff-edit"
          onClick={toggleForm}
          width={50}
        />
      )}
      <a href={linkUrl}>
        <div className={`tiles__item--image-container ${displayWidth}-image`}>
          <div
            className="tiles__item--image"
            style={{ backgroundImage: `url("${imageUrl || placeholderImage}")` }}
            role="img"
            aria-label={`${!imageUrl ? "placeholder" : "thumbnail"}`}
          />
        </div>
      </a>
      <div className="tiles__item--description">
        <h3>{name}</h3>
        <p>{description}</p>
      </div>
    </div>
  );

  return (
    <>
      {renderTile()}
      {showForm
        && (
          <TileForm
            closeModal={toggleForm}
            portalCardId={portalCardId}
            triggerCardsRefresh={triggerCardsRefresh}
          />
        )
      }
    </>
  );
};

export default Tile;
